import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Download, Loader2, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { A4_FORMAT, applyPrintPageRule } from '@/components/pnr/print-formats';
import type { DocumentGeneratedBy } from '@/lib/documents/types';
import { fmtDateTime } from '@/lib/documents/format';

// The print shell every supplier document (#809) opens in.
//
// SAME MECHANICS AS THE PNR PRINT SHEET, FOR THE SAME REASON. A new tab cannot
// re-authenticate (memory token cache — see pnr-print-sheet.tsx), so the document
// is a dialog in the tab that already holds the session, and "PDF" is the
// browser's own print dialog: `body.print-sheet-open` (index.css) hides the console
// and unwraps the dialog so `[data-print-sheet]` becomes the whole page. There is
// no PDF/XLSX library in the BFF on purpose — the browser prints to PDF, Excel
// opens the CSV.
//
// THE STAMP IS PART OF THE DOCUMENT. Who generated it and when is printed on the
// sheet itself (`DocumentStamp`), never only in the dialog chrome, because the
// chrome does not print and the supplier only ever sees the paper.

export function DocumentSheetDialog({
  open,
  onClose,
  title,
  description,
  ready,
  error,
  onDownloadCsv,
  downloading = false,
  testId,
  aside,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  /** The document's reads have settled and the sheet is drawn. */
  ready: boolean;
  /** Generation failed — the shell shows the error instead of the sheet. */
  error?: string | null;
  /** Absent for a document with no tabular export (the e-ticket): no CSV button. */
  onDownloadCsv?: () => void;
  downloading?: boolean;
  testId: string;
  /** Screen-only content under the sheet (never printed), e.g. the e-ticket's delivery panel. */
  aside?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation('disruptions');

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('print-sheet-open');
    return () => document.body.classList.remove('print-sheet-open');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    return applyPrintPageRule(A4_FORMAT);
  }, [open]);

  const printRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open && ready) printRef.current?.focus();
  }, [open, ready]);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="z-[70] flex h-[88dvh] w-[92vw] max-w-4xl flex-col gap-0 overflow-hidden p-0" data-print-shell data-testid={testId}>
        <DialogHeader className="border-b border-border px-5 py-3" data-print-chrome>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-4 w-4" aria-hidden />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto bg-neutral-100 p-6" data-print-scroll>
          {error ? (
            <p className="flex items-center gap-2 rounded-md border border-destructive/40 bg-background px-3 py-2 text-sm text-destructive" role="alert" data-testid={`${testId}-error`}>
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> {error}
            </p>
          ) : !ready ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" aria-busy="true" data-testid={`${testId}-loading`}>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {t('documents.preparing')}
            </p>
          ) : (
            children
          )}
          {aside ? (
            <div className="mx-auto mt-6 max-w-[210mm]" data-print-chrome>
              {aside}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3" data-print-chrome>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid={`${testId}-close`}>
            {t('documents.close')}
          </Button>
          {onDownloadCsv ? (
            <Button variant="outline" size="sm" disabled={!ready || downloading} onClick={onDownloadCsv} data-testid={`${testId}-csv`}>
              {downloading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden /> : <Download className="mr-1 h-3.5 w-3.5" aria-hidden />}
              {downloading ? t('documents.downloading') : t('documents.csv')}
            </Button>
          ) : null}
          <Button ref={printRef} size="sm" disabled={!ready} onClick={() => window.print()} data-testid={`${testId}-print`}>
            <Printer className="mr-1 h-3.5 w-3.5" aria-hidden />
            {t('documents.print')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** "Generado el … por …" — printed on the sheet, plus the exercise banner when the drill label is set (#812). */
export function DocumentStamp({
  generatedAt,
  generatedBy,
  exerciseLabel,
}: {
  generatedAt: string;
  generatedBy: DocumentGeneratedBy;
  exerciseLabel?: string | undefined;
}) {
  const { t, i18n } = useTranslation('disruptions');
  const who = generatedBy.displayName ?? generatedBy.operatorUrn;
  return (
    <footer className="mt-6 border-t border-neutral-300 pt-2 text-[11px] text-neutral-600" data-testid="document-stamp">
      <p>
        {who
          ? t('documents.generatedStamp', { when: fmtDateTime(generatedAt, i18n.language), who })
          : t('documents.generatedStampAnon', { when: fmtDateTime(generatedAt, i18n.language) })}
        {generatedBy.displayName && generatedBy.operatorUrn ? <span className="ml-1 font-mono text-[10px] text-neutral-500">{generatedBy.operatorUrn}</span> : null}
      </p>
      {exerciseLabel ? <p className="mt-0.5 font-semibold uppercase tracking-wide text-neutral-800">{t('documents.exercise', { label: exerciseLabel })}</p> : null}
    </footer>
  );
}

/** Drill banner at the top of a sheet — only when `exerciseLabel` is set (#812 wires it; here it is a hook). */
export function ExerciseBanner({ exerciseLabel }: { exerciseLabel?: string | undefined }) {
  const { t } = useTranslation('disruptions');
  if (!exerciseLabel) return null;
  return (
    <p className="mb-3 border-2 border-dashed border-neutral-800 px-3 py-1 text-center text-sm font-bold uppercase tracking-[0.2em]" data-testid="document-exercise">
      {t('documents.exercise', { label: exerciseLabel })}
    </p>
  );
}

/** Fail-soft hint when an upstream read did not answer — the document may be incomplete. */
export function UnknownHint({ known }: { known: Record<string, boolean> }) {
  const { t } = useTranslation('disruptions');
  const missing = Object.entries(known)
    .filter(([, ok]) => !ok)
    .map(([k]) => t(`documents.sources.${k}`));
  if (missing.length === 0) return null;
  return (
    <p className="mb-3 flex items-center gap-2 border border-amber-500 bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="document-unknown">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {t('documents.unknownHint', { sources: missing.join(', ') })}
    </p>
  );
}
